import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, Optional, ViewChild } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { ApplicationContextService } from 'src/app/core/services/application-context.service';
import { UserProfileService } from 'src/app/core/services/user-profile.service';

type SignatureAsset = {
  dataUrl: string;
  base64String: string;
  contentType: string;
};

@Component({
  selector: 'app-signature',
  templateUrl: './signature.component.html',
  styleUrls: ['./signature.component.css'],
})
export class SignatureComponent implements AfterViewInit, OnInit, OnDestroy {
  headingText = 'My Profile';
  typedName = '';
  isDrawMode = false;
  showSignatureForm = false;
  showFontSelector = false;
  isSaving = false;
  hasDrawnSignature = false;
  savedSignatureDataUrl: string | null = null;
  onClose2: Subject<void>;
  userFullName = '';
  userEmail = '';
  userPhone = '';
  organizationName = '';
  organizationAddress = '';
  userInitials = '';
  userPin = '';
  pinDigits: string[] = ['', '', '', ''];
  isSavingPin = false;
  showPinSection = false;
  private userId?: string;
  private readonly destroy$ = new Subject<void>();

  readonly signatureFonts: string[] = [
    'Great Vibes, cursive',
    'Dancing Script, cursive',
    'Allura, cursive',
    'Satisfy, cursive',
    'Pacifico, cursive',
    'Cookie, cursive',
    'Alex Brush, cursive',
    'Yellowtail, cursive',
    'Parisienne, cursive',
  ];
  currentFontIndex = 0;

  @ViewChild('signatureCanvas') canvasRef?: ElementRef<HTMLCanvasElement>;
  @ViewChild('pinInput0') pinInput0?: ElementRef<HTMLInputElement>;
  @ViewChild('pinInput1') pinInput1?: ElementRef<HTMLInputElement>;
  @ViewChild('pinInput2') pinInput2?: ElementRef<HTMLInputElement>;
  @ViewChild('pinInput3') pinInput3?: ElementRef<HTMLInputElement>;

  private ctx?: CanvasRenderingContext2D;
  private isDrawing = false;
  private resizeObserver?: ResizeObserver;
  
  private getPinInput(index: number): HTMLInputElement | null {
    switch (index) {
      case 0: return this.pinInput0?.nativeElement || null;
      case 1: return this.pinInput1?.nativeElement || null;
      case 2: return this.pinInput2?.nativeElement || null;
      case 3: return this.pinInput3?.nativeElement || null;
      default: return null;
    }
  }

  constructor(
    private readonly _applicationContextService: ApplicationContextService,
    private readonly _userProfileService: UserProfileService,
    @Optional() public bsModalRef?: BsModalRef
  ) {
    this.onClose2 = new Subject<void>();
  }

  get currentFont(): string {
    return this.signatureFonts[this.currentFontIndex];
  }

  ngOnInit(): void {
    this._applicationContextService.context
      .pipe(takeUntil(this.destroy$))
      .subscribe((context) => {
        const user = context?.currentlyLoggedInUser;
        if (!user) {
          return;
        }
        this.userId = user.id;
        const fullName = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
        this.userFullName = fullName || this.userFullName;
        this.userEmail = user.email ?? '';
        this.userPhone = user.phone ?? '';
        this.organizationName = user.companyName ?? '';
        this.organizationAddress = user.companyAddress ?? '';
        this.userInitials = `${user.firstName?.[0] ?? ''}${user.lastName?.[0] ?? ''}`.toUpperCase();
        // Load PIN if available (assuming it's stored in user object)
        const pin = (user as any).pin ?? '';
        this.userPin = pin;
        // Initialize pin digits array
        if (pin && pin.length === 4) {
          this.pinDigits = pin.split('');
        } else {
          this.pinDigits = ['', '', '', ''];
        }
        const signature = user.signature;
        if (signature?.base64String) {
          this.savedSignatureDataUrl = this.createDataUrl(signature.base64String, signature.contentType);
        }

        if (!this.typedName.trim() && fullName) {
          this.typedName = fullName;
        }
      });
  }

  openSignatureForm(): void {
    this.headingText = 'Add Your Signature';
    this.showSignatureForm = true;
    this.isDrawMode = false;
    this.showFontSelector = false;
    this.isSaving = false;
  }

  ngAfterViewInit(): void {
    if (this.isDrawMode) {
      this.initializeCanvas();
    }
  }

  onNameInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.typedName = input.value;
  }

  enterDrawMode(): void {
    this.headingText = 'Draw Your Signature';
    this.showSignatureForm = true;
    this.showFontSelector = false;
    this.isDrawMode = true;
    this.hasDrawnSignature = false;
    setTimeout(() => this.initializeCanvas(), 0);
  }

  exitDrawMode(): void {
    this.headingText = 'Add Your Signature';
    this.isDrawMode = false;
    this.isDrawing = false;
    this.teardownResizeObserver();
  }

  startDrawing(event: MouseEvent): void {
    if (!this.ctx || !this.canvasRef) {
      return;
    }
    this.isDrawing = true;
    const { x, y } = this.getRelativePosition(event);
    this.ctx.beginPath();
    this.ctx.moveTo(x, y);
    event.preventDefault();
  }

  draw(event: MouseEvent): void {
    if (!this.isDrawing || !this.ctx) {
      return;
    }
    const { x, y } = this.getRelativePosition(event);
    this.ctx.lineTo(x, y);
    this.ctx.stroke();
    if (!this.hasDrawnSignature) {
      this.hasDrawnSignature = true;
    }
  }

  stopDrawing(): void {
    if (!this.isDrawing) {
      return;
    }
    this.isDrawing = false;
    this.ctx?.closePath();
  }

  clearCanvas(): void {
    const canvas = this.canvasRef?.nativeElement;
    if (!canvas || !this.ctx) {
      return;
    }
    this.ctx.clearRect(0, 0, canvas.width, canvas.height);
    this.hasDrawnSignature = false;
  }

  openFontSelector(): void {
    this.showFontSelector = true;
    this.isDrawMode = false;
    this.headingText = 'Choose Signature Style';
  }

  closeFontSelector(): void {
    this.showFontSelector = false;
    this.headingText = this.showSignatureForm ? 'Add Your Signature' : 'My Profile';
  }

  selectFont(index: number): void {
    this.currentFontIndex = index;
    this.closeFontSelector();
  }

  get signatureActionLabel(): string {
    return this.savedSignatureDataUrl ? 'Update Signature' : 'Add Your Signature';
  }

  get canSaveSignature(): boolean {
    if (this.isDrawMode) {
      return this.hasDrawnSignature;
    }
    return !!this.typedName.trim();
  }

  get hasPin(): boolean {
    return !!this.userPin && this.userPin.length === 4;
  }

  onSaveSignature(): void {
    if (this.isSaving) {
      return;
    }

    if (!this.userId) {
console.error('Cannot save signature: missing userId.');
      return;
    }

    let signatureAsset: SignatureAsset | null = null;

    if (this.isDrawMode) {
      const canvas = this.canvasRef?.nativeElement;
      if (!canvas) {
return;
      }
      if (!this.hasDrawnSignature) {
return;
      }
      signatureAsset = this.prepareCanvasSignatureAsset(canvas);
    } else {
      const signatureText = this.typedName.trim();
      if (!signatureText) {
return;
      }
      signatureAsset = this.generateTypedSignatureAsset(signatureText, this.currentFont);
      if (!signatureAsset) {
return;
      }
    }

    if (!signatureAsset) {
return;
    }

    const { dataUrl, base64String, contentType } = signatureAsset;

    this.isSaving = true;
    this._userProfileService
      .updateSignature(this.userId, {
        signature: {
          base64String,
          contentType,
        },
      })
      .pipe(
        finalize(() => {
          this.isSaving = false;
        })
      )
      .subscribe({
        next: () => {
this.savedSignatureDataUrl = dataUrl;
          this.showProfileView();
        },
        error: (error) => {
          console.error('Error saving signature', error);
},
      });
  }

  private initializeCanvas(): void {
    const canvas = this.canvasRef?.nativeElement;
    if (!canvas) {
      return;
    }

    const container = canvas.parentElement;
    const width = container?.clientWidth ?? 640;
    const height = 260;
    const ratio = window.devicePixelRatio || 1;

    canvas.width = width * ratio;
    canvas.height = height * ratio;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    const context = canvas.getContext('2d');
    if (!context) {
      return;
    }
    context.clearRect(0, 0, canvas.width, canvas.height);

    context.setTransform(1, 0, 0, 1, 0, 0);
    context.scale(ratio, ratio);
    context.lineWidth = 2.5;
    context.lineJoin = 'round';
    context.lineCap = 'round';
    context.strokeStyle = '#000000';

    this.ctx = context;
    this.hasDrawnSignature = false;

    this.attachResizeObserver();
  }

  private getRelativePosition(event: MouseEvent): { x: number; y: number } {
    const canvas = this.canvasRef!.nativeElement;
    const rect = canvas.getBoundingClientRect();

    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  }

  private attachResizeObserver(): void {
    if (this.resizeObserver || !this.canvasRef?.nativeElement.parentElement || typeof ResizeObserver === 'undefined') {
      return;
    }

    this.resizeObserver = new ResizeObserver(() => this.initializeCanvas());
    this.resizeObserver.observe(this.canvasRef.nativeElement.parentElement);
  }

  private teardownResizeObserver(): void {
    if (!this.resizeObserver) {
      return;
    }
    this.resizeObserver.disconnect();
    this.resizeObserver = undefined;
  }

  private extractBase64FromDataUrl(dataUrl: string): string {
    const commaIndex = dataUrl.indexOf(',');
    if (commaIndex === -1) {
      return '';
    }
    return dataUrl.substring(commaIndex + 1);
  }

  private createDataUrl(base64String: string, contentType?: string): string {
    const type = contentType || 'image/png';
    return `data:${type};base64,${base64String}`;
  }

  private generateTypedSignatureAsset(text: string, fontFamily: string): SignatureAsset | null {
    const canvas = this.createTypedSignatureCanvas(text, fontFamily);
    if (!canvas) {
      return null;
    }
    return this.buildSignatureAssetFromCanvas(canvas);
  }

  private prepareCanvasSignatureAsset(canvas: HTMLCanvasElement): SignatureAsset | null {
    const clone = document.createElement('canvas');
    clone.width = canvas.width;
    clone.height = canvas.height;
    const cloneCtx = clone.getContext('2d');
    if (!cloneCtx) {
      return null;
    }
    cloneCtx.drawImage(canvas, 0, 0);
    return this.buildSignatureAssetFromCanvas(clone);
  }

  private createTypedSignatureCanvas(text: string, fontFamily: string): HTMLCanvasElement | null {
    const ratio = window.devicePixelRatio || 1;
    const baseWidth = 800;
    const baseHeight = 300;

    const canvas = document.createElement('canvas');
    canvas.width = baseWidth * ratio;
    canvas.height = baseHeight * ratio;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return null;
    }

    ctx.scale(ratio, ratio);
    ctx.clearRect(0, 0, baseWidth, baseHeight);
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'center';

    const maxWidth = baseWidth * 0.9;
    let fontSize = 96;
    ctx.font = `${fontSize}px ${fontFamily}`;
    let metrics = ctx.measureText(text);

    while (metrics.width > maxWidth && fontSize > 24) {
      fontSize -= 2;
      ctx.font = `${fontSize}px ${fontFamily}`;
      metrics = ctx.measureText(text);
    }

    const ascent = metrics.actualBoundingBoxAscent ?? fontSize * 0.8;
    const descent = metrics.actualBoundingBoxDescent ?? fontSize * 0.2;
    const baseline = (baseHeight - (ascent + descent)) / 2 + ascent;

    ctx.fillStyle = '#000000';
    ctx.fillText(text, baseWidth / 2, baseline);

    return canvas;
  }

  private buildSignatureAssetFromCanvas(canvas: HTMLCanvasElement): SignatureAsset | null {
    const trimmedCanvas = this.trimCanvas(canvas) ?? canvas;
    const contentType = 'image/png';
    const dataUrl = trimmedCanvas.toDataURL(contentType);
    const base64String = this.extractBase64FromDataUrl(dataUrl);

    if (!base64String) {
      return null;
    }

    return {
      dataUrl,
      base64String,
      contentType,
    };
  }

  private trimCanvas(source: HTMLCanvasElement): HTMLCanvasElement | null {
    const ctx = source.getContext('2d');
    if (!ctx) {
      return null;
    }

    const { width, height } = source;
    if (!width || !height) {
      return null;
    }

    const imageData = ctx.getImageData(0, 0, width, height);
    const data = imageData.data;

    let minX = width;
    let minY = height;
    let maxX = 0;
    let maxY = 0;

    for (let y = 0; y < height; y += 1) {
      for (let x = 0; x < width; x += 1) {
        const index = (y * width + x) * 4;
        const alpha = data[index + 3];
        if (alpha > 8) {
          if (x < minX) {
            minX = x;
          }
          if (y < minY) {
            minY = y;
          }
          if (x > maxX) {
            maxX = x;
          }
          if (y > maxY) {
            maxY = y;
          }
        }
      }
    }

    if (maxX < minX || maxY < minY) {
      return null;
    }

    const trimWidth = maxX - minX + 1;
    const trimHeight = maxY - minY + 1;

    const trimmedCanvas = document.createElement('canvas');
    trimmedCanvas.width = trimWidth;
    trimmedCanvas.height = trimHeight;
    const trimmedCtx = trimmedCanvas.getContext('2d');
    if (!trimmedCtx) {
      return null;
    }

    trimmedCtx.drawImage(source, minX, minY, trimWidth, trimHeight, 0, 0, trimWidth, trimHeight);

    return trimmedCanvas;
  }

  private showProfileView(): void {
    this.headingText = 'My Profile';
    this.showSignatureForm = false;
    this.showFontSelector = false;
    this.isDrawMode = false;
    this.isDrawing = false;
    this.hasDrawnSignature = false;
    this.teardownResizeObserver();
  }

  closeModal(): void {
    this.onClose2.next();
    if (this.bsModalRef) {
      this.bsModalRef.hide();
    }
  }

  onPinInput(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    // Stop event propagation to prevent multiple triggers
    event.stopPropagation();
    
    // Get the current value
    let value = input.value;
    
    // Only allow digits
    value = value.replace(/\D/g, '');
    
    // Limit to single digit - take only the last character if multiple
    if (value.length > 1) {
      value = value.slice(-1);
    }
    
    // Update the digit at this index only - don't update others
    const previousValue = this.pinDigits[index];
    this.pinDigits[index] = value;
    
    // Only update if value actually changed
    if (previousValue !== value) {
      // Update the combined PIN string
      this.userPin = this.pinDigits.join('');
      
      // Only auto-focus next box if a digit was actually entered and we're not on the last box
      if (value && index < 3) {
        // Use setTimeout to ensure the current input is updated first
        setTimeout(() => {
          const nextInput = this.getPinInput(index + 1);
          if (nextInput && !nextInput.value) {
            nextInput.focus();
          }
        }, 10);
      }
    }
  }

  onPinKeyDown(event: KeyboardEvent, index: number): void {
    const input = event.target as HTMLInputElement;
    
    // Handle backspace - move to previous box if current is empty
    if (event.key === 'Backspace' && !input.value && index > 0) {
      event.preventDefault();
      const prevInput = this.getPinInput(index - 1);
      if (prevInput) {
        prevInput.focus();
        prevInput.value = '';
        this.pinDigits[index - 1] = '';
        this.userPin = this.pinDigits.join('');
      }
    }
    
    // Handle arrow keys
    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      const prevInput = this.getPinInput(index - 1);
      if (prevInput) {
        prevInput.focus();
      }
    }
    
    if (event.key === 'ArrowRight' && index < 3) {
      event.preventDefault();
      const nextInput = this.getPinInput(index + 1);
      if (nextInput) {
        nextInput.focus();
      }
    }
  }

  onPinPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pastedData = event.clipboardData?.getData('text') || '';
    const digits = pastedData.replace(/\D/g, '').slice(0, 4).split('');
    
    // Fill the boxes with pasted digits
    for (let i = 0; i < 4; i++) {
      this.pinDigits[i] = digits[i] || '';
    }
    
    // Update combined PIN
    this.userPin = this.pinDigits.join('');
    
    // Update input values and focus using ViewChild references
    for (let i = 0; i < 4; i++) {
      const input = this.getPinInput(i);
      if (input) {
        input.value = this.pinDigits[i] || '';
      }
    }
    
    // Focus the last filled box or first empty box
    const firstEmptyIndex = this.pinDigits.findIndex(d => !d);
    const focusIndex = firstEmptyIndex === -1 ? 3 : firstEmptyIndex;
    const focusInput = this.getPinInput(focusIndex);
    if (focusInput) {
      focusInput.focus();
    }
  }

  onShowPinSection(): void {
    // If pin exists, populate the pin digits for viewing/editing
    if (this.hasPin && this.userPin) {
      this.pinDigits = this.userPin.split('');
    } else {
      // Reset pin digits for new pin entry
      this.pinDigits = ['', '', '', ''];
      this.userPin = '';
    }
    this.showPinSection = true;
  }

  onHidePinSection(): void {
    this.showPinSection = false;
  }

  onSavePin(): void {
    if (this.isSavingPin) {
      return;
    }

    if (!this.userId) {
return;
    }

    if (!this.userPin || this.userPin.length !== 4) {
return;
    }

    this.isSavingPin = true;
    this._userProfileService
      .updatePin(this.userId, { pin: this.userPin })
      .pipe(
        finalize(() => {
          this.isSavingPin = false;
        })
      )
      .subscribe({
        next: () => {
// Hide PIN section after successful save
          this.showPinSection = false;
        },
        error: (error) => {
          console.error('Error saving PIN', error);
},
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.teardownResizeObserver();
    this.onClose2.complete();
  }
}
